import mammoth from "mammoth";
import JSZip from "jszip";
import {
  NARRATIVE_FONT_SIZE,
  NARRATIVE_LIST_INDENT,
  NARRATIVE_LIST_COUNTER,
  TABLE_ALIGN_MARGINS,
  MAX_CELL_BORDER_WIDTH,
} from "./narrativeStyles";

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

// 1440 twip = 1 inch = 96px
const TWIPS_PER_PX = 15;

// w:ind의 *Chars 계열은 글자 폭의 1/100 단위다 (한글 문서가 들여쓰기를 적는 흔한 방식)
const CHAR_UNITS_PER_CHAR = 100;

// w:tblW의 pct는 백분율의 1/50 단위다 (5000 = 100%). "100%"처럼 단위를 붙여 적는
// 문서도 있어(문서 생성 라이브러리가 흔히 그렇게 쓴다) 양쪽을 모두 읽는다.
const PCT_UNITS_PER_PERCENT = 50;

// mammoth는 의미 기반 변환이라 밑줄·형광펜은 스타일 맵으로 명시해야 살아남는다
const STYLE_MAP = ["u => u", "highlight => mark"];

const BLOCK_TAGS = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "BLOCKQUOTE", "PRE"]);

// w:p 하나가 대응되는 HTML 블록
const PARAGRAPH_BLOCKS = "p, h1, h2, h3, h4, h5, h6, li, blockquote, pre";

// 블록 안에 중첩된 블록(하위 목록 등) — 텍스트 비교와 span 래핑에서 제외한다
const NESTED_BLOCKS = "ul, ol, table, p, h1, h2, h3, h4, h5, h6, blockquote, pre";

// mammoth가 빈 문단을 버리는 만큼만 건너뛰며 대응을 찾는다
const PARAGRAPH_LOOKAHEAD = 5;

// w:basedOn 사슬 길이 상한 (순환·비정상 문서 방어)
const STYLE_CHAIN_LIMIT = 20;

const normalizeText = (text) => text.replace(/\s+/g, " ").trim();

const getWordAttr = (el, name) =>
  el.getAttributeNS(W_NS, name) || el.getAttribute(`w:${name}`) || el.getAttribute(name);

const wordChildren = (el, localName) =>
  el ? Array.from(el.children).filter((c) => c.namespaceURI === W_NS && c.localName === localName) : [];

const wordChild = (el, localName) => wordChildren(el, localName)[0] || null;

const intAttr = (el, name) => {
  const value = parseInt(el ? getWordAttr(el, name) : "", 10);
  return Number.isFinite(value) ? value : null;
};

// <w:pStyle w:val="..."> 처럼 자식 원소의 val 하나만 필요한 경우
const childVal = (el, localName) => {
  const child = wordChild(el, localName);
  return child ? getWordAttr(child, "val") : null;
};

// 블록 자신의 텍스트 노드만 (중첩 목록·표 안의 텍스트는 제외).
// 런 단위로 글자 크기를 입힐 때 이 순서대로 오프셋을 센다.
const ownTextNodes = (el) => {
  const nodes = [];
  const walk = (parent) => {
    parent.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        if (node.nodeValue) nodes.push(node);
      } else if (node.nodeType === 1 && !node.matches(NESTED_BLOCKS)) {
        walk(node);
      }
    });
  };
  walk(el);
  return nodes;
};

const ownText = (el) => ownTextNodes(el).map((node) => node.nodeValue).join("");

// .docx 안의 XML 파트를 문서로 읽는다. mammoth가 버리는 서식은 여기서 직접 뽑아 되살린다.
export const readWordXml = async (arrayBuffer) => {
  const parse = (zip, path) => {
    const file = zip.file(path);
    if (!file) return Promise.resolve(null);
    return file
      .async("text")
      .then((text) => new DOMParser().parseFromString(text, "application/xml"));
  };

  try {
    const zip = await JSZip.loadAsync(arrayBuffer);
    const [document, styles, numbering] = await Promise.all([
      parse(zip, "word/document.xml"),
      parse(zip, "word/styles.xml"),
      parse(zip, "word/numbering.xml"),
    ]);
    return { document, styles, numbering };
  } catch {
    return { document: null, styles: null, numbering: null };
  }
};

// w:jc는 논리적 방향(start/end)과 물리적 방향(left/right)을 섞어 쓰고,
// 양쪽 맞춤은 both/distribute로 적는다. CSS text-align 값으로 옮긴다.
const WORD_ALIGNMENTS = {
  left: "left",
  start: "left",
  center: "center",
  right: "right",
  end: "right",
  both: "justify",
  distribute: "justify",
};

// 표 자체의 좌우 위치(w:tblPr/w:jc). 문단과 달리 양쪽 맞춤은 없다.
const WORD_TABLE_ALIGNMENTS = {
  left: null, // 기본값이라 굳이 적지 않는다
  start: null,
  center: "center",
  right: "right",
  end: "right",
};

// w:vAlign(셀 세로 맞춤) → CSS vertical-align. both(양쪽 맞춤)는 Word에서도
// 가운데처럼 보이므로 middle로 옮긴다.
const WORD_VERTICAL_ALIGNMENTS = {
  top: "top",
  center: "middle",
  both: "middle",
  bottom: "bottom",
};

/**
 * w:pPr에서 화면에 옮길 수 있는 값만 뽑는다.
 *
 * 지정되지 않은 항목은 키 자체를 만들지 않는다. 문서 기본값 → 스타일 → 직접 지정 순으로
 * 얕은 병합(spread)해서 쓰기 때문에, undefined를 넣어 두면 상위에서 내려온 값을 지워 버린다.
 */
const readParagraphProps = (pPr) => {
  const props = {};
  if (!pPr) return props;

  const jc = wordChild(pPr, "jc");
  const align = jc && WORD_ALIGNMENTS[(getWordAttr(jc, "val") || "").toLowerCase()];
  if (align) props.align = align;

  const ind = wordChild(pPr, "ind");
  if (ind) {
    const twips = intAttr(ind, "left") !== null ? intAttr(ind, "left") : intAttr(ind, "start");
    if (twips !== null) props.indentTwips = twips;

    const chars = intAttr(ind, "leftChars") !== null ? intAttr(ind, "leftChars") : intAttr(ind, "startChars");
    if (chars !== null) props.indentChars = chars;

    // 내어쓰기(w:hanging)는 첫 줄만 왼쪽으로 끌어낸다. 목록에서는 그 자리가 글머리 기호 자리다.
    const hanging = intAttr(ind, "hanging");
    if (hanging !== null) props.hangingTwips = hanging;

    const hangingChars = intAttr(ind, "hangingChars");
    if (hangingChars !== null) props.hangingChars = hangingChars;
  }

  // 어느 목록(w:numId)의 몇 번째 단계(w:ilvl)인지. numbering.xml에서 기호와 들여쓰기를 찾는 열쇠다.
  const numPr = wordChild(pPr, "numPr");
  if (numPr) {
    const numId = intAttr(wordChild(numPr, "numId"), "val");
    if (numId !== null) props.numId = numId;

    const ilvl = intAttr(wordChild(numPr, "ilvl"), "val");
    if (ilvl !== null) props.ilvl = ilvl;
  }

  return props;
};

const HEX_COLOR = /^[0-9a-fA-F]{6}$/;

// #rrggbb로 옮길 수 있는 값만. "auto"(문서 기본값)는 물려받은 값을 끄라는 뜻이라 null,
// 풀 수 없는 값(w:themeColor만 적힌 경우 등)은 undefined라 상위에서 내려온 값이 남는다.
const readColorValue = (raw) => {
  const val = (raw || "").trim();
  if (/^auto$/i.test(val)) return null;
  return HEX_COLOR.test(val) ? `#${val.toLowerCase()}` : undefined;
};

// CSS font-family에 그대로 넣을 수 없는 이름은 버린다. 글꼴 이름에는 없는 문자들이라
// 걸리는 건 사실상 깨진 문서뿐이다.
const UNSAFE_FONT_NAME = /['"\\;{}<>]/;

const SERIF_FONT_NAME = /명조|바탕|궁서|myeongjo|batang|gungs|times|georgia|garamond|palatino|cambria|constantia|serif/i;
const MONOSPACE_FONT_NAME = /고정폭|courier|consolas|monaco|menlo|d2coding|mono/i;

/**
 * 목록의 이름이 하나도 안 풀렸을 때 떨어질 자리.
 *
 * 인라인 font-family는 상속을 "대체"한다. 제네릭으로 닫지 않으면 그 글꼴이 없는 PC에서
 * 편집기 기본 글꼴(고딕)이 아니라 브라우저 기본 글꼴(대개 명조)로 떨어져, 고딕으로 쓴
 * 문서가 명조로 보인다. 이름으로 계열을 어림잡는데, 이 어림이 쓰이는 건 글꼴이 실제로
 * 없을 때뿐이라(있으면 앞의 이름이 이긴다) 틀려도 지금보다 나빠지지 않는다.
 * 툴바 FONT_FAMILIES 프리셋과 같은 규칙이다.
 */
const genericFallback = (names) => {
  if (names.some((name) => MONOSPACE_FONT_NAME.test(name))) return "monospace";
  // "Sans Serif"처럼 이름에 sans가 든 글꼴이 serif로 잡히지 않게 한다
  const serif = names.some((name) => !/sans/i.test(name) && SERIF_FONT_NAME.test(name));
  return serif ? "serif" : "sans-serif";
};

/**
 * w:rFonts를 CSS 글꼴 목록으로. Word는 글꼴을 문자 종류별로 나눠 적으므로(ascii는 라틴,
 * eastAsia는 한글·한자) 하나만 고르면 반쪽이 된다. 적힌 순서대로 목록에 넣으면 브라우저가
 * 글자마다 알아서 골라 Word와 같게 보인다.
 *
 * *Theme 계열(w:asciiTheme 등)만 적힌 런은 theme1.xml 없이는 풀 수 없어 건드리지 않는다.
 */
const readFontFamily = (rFonts) => {
  if (!rFonts) return undefined;

  const names = ["ascii", "eastAsia", "hAnsi", "cs"]
    .map((slot) => (getWordAttr(rFonts, slot) || "").trim())
    .filter((name) => name && !UNSAFE_FONT_NAME.test(name));

  const unique = [...new Set(names)];
  if (!unique.length) return undefined;

  // 작은따옴표로 감싼다 (FontFamily 확장이 style 속성 문자열을 그대로 들고 있어,
  // 큰따옴표를 쓰면 직렬화될 때 &quot;로 바뀐다)
  return [...unique.map((name) => `'${name}'`), genericFallback(unique)].join(", ");
};

/**
 * w:rPr에서 화면에 옮길 수 있는 글자 서식을 뽑는다. readParagraphProps와 같은 규칙으로,
 * 지정되지 않은 항목은 키를 만들지 않는다 (얕은 병합으로 상속시키기 때문).
 *
 * w:sz는 half-point 단위라 pt = val / 2, px = pt * 96 / 72.
 */
const readRunProps = (rPr) => {
  const props = {};

  const halfPoints = intAttr(wordChild(rPr, "sz"), "val");
  if (halfPoints > 0) props.fontSize = Math.round((halfPoints * 2) / 3);

  const color = wordChild(rPr, "color");
  if (color) {
    const value = readColorValue(getWordAttr(color, "val"));
    if (value !== undefined) props.color = value;
  }

  // 글자 배경(w:shd). 형광펜(w:highlight)과 달리 임의의 색을 쓸 수 있고, mammoth는
  // 형광펜만 <mark>로 옮긴다. w:val="nil"은 칠하지 않는다는 뜻이고, 그 밖에는 w:fill이
  // 배경색이다(무늬는 화면에 옮길 수 없어 색만 가져온다).
  const shd = wordChild(rPr, "shd");
  if (shd) {
    const nil = /^nil$/i.test((getWordAttr(shd, "val") || "").trim());
    const value = nil ? null : readColorValue(getWordAttr(shd, "fill"));
    if (value !== undefined) props.background = value;
  }

  const fontFamily = readFontFamily(wordChild(rPr, "rFonts"));
  if (fontFamily !== undefined) props.fontFamily = fontFamily;

  return props;
};

// 셀 테두리가 걸리는 네 면과, 표 테두리에서 그 면을 담당하는 이름.
// 표의 안쪽 가로줄(insideH)은 셀의 위/아래, 세로줄(insideV)은 왼/오른쪽이 된다.
const BORDER_SIDES = { top: "insideH", bottom: "insideH", left: "insideV", right: "insideV" };

// w:val → CSS border-style. 목록에 없는 무늬(파도선 등)는 실선으로 옮긴다.
const WORD_BORDER_STYLES = {
  nil: "none",
  none: "none",
  double: "double",
  dashed: "dashed",
  dotted: "dotted",
  dotDash: "dashed",
  dotDotDash: "dashed",
  dashSmallGap: "dashed",
  dashDotStroked: "dashed",
};

// <w:top w:val="single" w:sz="8" w:color="000000"/> → "1px solid #000000"
const readBorder = (el) => {
  const val = (getWordAttr(el, "val") || "").trim();
  if (!val) return null;

  const style = WORD_BORDER_STYLES[val] || "solid";
  if (style === "none") return "none";

  // w:sz는 1/8 pt 단위. px = sz / 8 * 96 / 72 = sz / 6.
  const eighths = intAttr(el, "sz");
  const width = eighths > 0 ? Math.min(Math.max(Math.round(eighths / 6), 1), MAX_CELL_BORDER_WIDTH) : 1;

  const color = (getWordAttr(el, "color") || "").trim();
  return `${width}px ${style} ${color && color !== "auto" ? `#${color}` : "#000000"}`;
};

// w:tblBorders / w:tcBorders에서 지정된 면만 뽑는다
const readBorders = (bordersEl) => {
  if (!bordersEl) return {};

  return ["top", "bottom", "left", "right", "insideH", "insideV"].reduce((acc, name) => {
    const el = wordChild(bordersEl, name);
    const value = el && readBorder(el);
    if (value) acc[name] = value;
    return acc;
  }, {});
};

// w:tblPr에서 표의 좌우 위치와 테두리. 왼쪽(기본값)도 값을 남겨야 스타일에서 내려온
// 가운데 맞춤을 되돌릴 수 있다.
const readTableProps = (tblPr) => {
  const props = { borders: readBorders(wordChild(tblPr, "tblBorders")) };

  const jc = (childVal(tblPr, "jc") || "").toLowerCase();
  if (jc in WORD_TABLE_ALIGNMENTS) props.align = WORD_TABLE_ALIGNMENTS[jc];

  return props;
};

/**
 * styles.xml에서 문단·글자 서식을 스타일 id별로 읽는다.
 *
 * Word는 정렬·글자 크기·들여쓰기를 문단에 직접 적는 대신 스타일에 담아 두는 일이 훨씬
 * 많다. 이 표 없이 직접 지정(w:pPr/w:rPr)만 읽으면 대부분의 서식이 그대로 사라진다.
 */
export const parseStyleDefinitions = (stylesDoc) => {
  const styles = {
    defaults: { paragraph: {}, run: {} },
    byId: new Map(),
    cache: new Map(),
  };
  if (!stylesDoc || !stylesDoc.documentElement) return styles;

  const root = stylesDoc.documentElement;

  const docDefaults = wordChild(root, "docDefaults");
  if (docDefaults) {
    styles.defaults = {
      paragraph: readParagraphProps(wordChild(wordChild(docDefaults, "pPrDefault"), "pPr")),
      run: readRunProps(wordChild(wordChild(docDefaults, "rPrDefault"), "rPr")),
    };
  }

  wordChildren(root, "style").forEach((style) => {
    const id = getWordAttr(style, "styleId");
    if (!id) return;

    styles.byId.set(id, {
      basedOn: childVal(style, "basedOn"),
      paragraph: readParagraphProps(wordChild(style, "pPr")),
      run: readRunProps(wordChild(style, "rPr")),
      table: readTableProps(wordChild(style, "tblPr")),
    });
  });

  return styles;
};

// w:basedOn 사슬을 뿌리부터 훑어 합친 서식. 파생 스타일이 부모를 덮는다.
const resolveStyle = (styles, styleId) => {
  const empty = { paragraph: {}, run: {}, table: {} };
  if (!styleId || !styles.byId.size) return empty;
  if (styles.cache.has(styleId)) return styles.cache.get(styleId);

  const chain = [];
  const seen = new Set();
  for (let id = styleId; id && chain.length < STYLE_CHAIN_LIMIT && !seen.has(id); ) {
    const style = styles.byId.get(id);
    if (!style) break;
    seen.add(id);
    chain.unshift(style);
    id = style.basedOn;
  }

  const resolved = chain.reduce(
    (acc, style) => ({
      paragraph: { ...acc.paragraph, ...style.paragraph },
      run: { ...acc.run, ...style.run },
      table: { ...acc.table, ...style.table },
    }),
    empty
  );

  styles.cache.set(styleId, resolved);
  return resolved;
};

// 화면에서 기본 글머리 기호로 낼 수 없어 직접 그려야 하는 목록 모양.
// narrativeStyles의 [data-list-style] 규칙과 짝이다.
const CHECK_BULLETS = new Set(["✓", "✔", "✅", "√", "☑", "🗹"]);

/**
 * 기호 글꼴의 몇 번째 문자인지.
 *
 * Word는 Wingdings 같은 기호 글꼴의 문자를 사용자 정의 영역(U+F000~U+F0FF)에 얹어 적는
 * 일이 많다(0xFC -> U+F0FC). 글꼴 안에서의 번호로 되돌려야 어느 쪽으로 적힌 문서든 같게 읽는다.
 */
const symbolCode = (char) => {
  const code = char.charCodeAt(0);
  return code >= 0xf000 && code <= 0xf0ff ? code - 0xf000 : code;
};

// 글꼴별 체크 표시 문자 번호. Wingdings의 0xFC(체크)·0xFD(굵은 체크)·0xFE(체크 상자)가
// Word 기호 목록에서 ✓를 고를 때 나오는 값이다.
const CHECK_SYMBOLS = [
  // Wingdings 2는 0x50(체크)·0x51(굵은 체크)이고, 그 다음 0x52부터는 ✗다
  [/wingdings\s*2/i, [0x50, 0x51]],
  [/wingdings/i, [0xfc, 0xfd, 0xfe]],
  [/marlett/i, [0x61]],
];

// 사용자 정의 영역으로 적힌 문자는 글꼴 이름이 빠져 있어도 Wingdings로 본다
// (그 영역을 쓰는 목록 기호가 사실상 Wingdings다)
const isCheckBullet = (text, font) => {
  if (CHECK_BULLETS.has(text)) return true;

  const code = symbolCode(text);
  const match = CHECK_SYMBOLS.find(([pattern]) => pattern.test(font || ""));
  if (match) return match[1].includes(code);

  return text.charCodeAt(0) >= 0xf000 && [0xfc, 0xfd, 0xfe].includes(code);
};

// "%1"처럼 단계 번호가 들어갈 자리. 이게 없으면 번호가 아니라 기호다.
const NUMBER_PLACEHOLDER = /%\d/;

// "%1)" 처럼 번호 뒤에 닫는 괄호만 붙는 형태
const PAREN_NUMBER = /^%\d\)$/;

// w:lvlText(기호 문자열)와 글꼴로 목록 모양을 가린다. 아는 모양이 아니면 기본 기호에 맡긴다.
const markerStyle = (numFmt, lvlText, font) => {
  const text = (lvlText || "").trim();
  if (!text) return null;

  // w:numFmt를 적지 않은 문서도 있다. 번호 자리가 없으면 기호로 본다.
  if (numFmt === "bullet" || (!numFmt && !NUMBER_PLACEHOLDER.test(text))) {
    return isCheckBullet(text, font) ? "check" : null;
  }

  return numFmt === "decimal" && PAREN_NUMBER.test(text) ? "paren" : null;
};

// w:rFonts는 문자 종류별로 글꼴을 나눠 적는다. 기호 글꼴은 어디에 적혀 있든 하나뿐이다.
const levelFont = (lvl) => {
  const rFonts = wordChild(wordChild(lvl, "rPr"), "rFonts");
  if (!rFonts) return null;
  return getWordAttr(rFonts, "ascii") || getWordAttr(rFonts, "hAnsi") || getWordAttr(rFonts, "cs");
};

// w:lvl 하나 = 목록의 한 단계. 기호 모양, 시작 번호, 그 단계의 들여쓰기를 읽는다.
const readNumberingLevel = (lvl) => {
  const start = intAttr(wordChild(lvl, "start"), "val");
  return {
    style: markerStyle(childVal(lvl, "numFmt"), childVal(lvl, "lvlText"), levelFont(lvl)),
    start: start > 0 ? start : 1,
    paragraph: readParagraphProps(wordChild(lvl, "pPr")),
  };
};

const levelMap = (levels) =>
  levels.reduce((map, lvl) => {
    const ilvl = intAttr(lvl, "ilvl");
    if (ilvl !== null) map.set(ilvl, readNumberingLevel(lvl));
    return map;
  }, new Map());

/**
 * numbering.xml에서 목록 번호(w:numId)별 단계 정의를 읽는다.
 *
 * 목록의 기호 모양·시작 번호·들여쓰기는 문단이 아니라 여기에 적혀 있다. mammoth는 이 파일을
 * ul/ol 중 무엇인지와 몇 단계인지를 정하는 데만 쓰고 버리므로, ✓·"1)" 같은 기호와 기호가
 * 놓이는 자리, 그리고 번호가 몇부터 이어지는지는 직접 읽어야 한다.
 */
export const parseNumberingDefinitions = (numberingDoc) => {
  const byNumId = new Map();
  if (!numberingDoc || !numberingDoc.documentElement) return byNumId;

  const root = numberingDoc.documentElement;

  const abstract = wordChildren(root, "abstractNum").reduce((map, node) => {
    const id = intAttr(node, "abstractNumId");
    if (id !== null) map.set(id, levelMap(wordChildren(node, "lvl")));
    return map;
  }, new Map());

  wordChildren(root, "num").forEach((node) => {
    const numId = intAttr(node, "numId");
    if (numId === null) return;

    const abstractId = intAttr(wordChild(node, "abstractNumId"), "val");
    const base = abstract.get(abstractId) || new Map();

    // w:lvlOverride는 이 목록에서만 특정 단계를 다시 정의한다.
    // w:startOverride는 "여기서부터 번호를 다시(또는 N부터) 센다"는 뜻이다.
    const overrideNodes = wordChildren(node, "lvlOverride");
    const levels = levelMap(overrideNodes.map((el) => wordChild(el, "lvl")).filter(Boolean));

    const starts = overrideNodes.reduce((map, el) => {
      const ilvl = intAttr(el, "ilvl");
      const start = intAttr(wordChild(el, "startOverride"), "val");
      if (ilvl !== null && start > 0) map.set(ilvl, start);
      return map;
    }, new Map());

    byNumId.set(numId, {
      // 번호는 목록 정의(abstractNum)마다 이어진다. 같은 정의를 가리키는 w:num이 여럿이면
      // (Word가 "이전 목록에 이어서"를 이렇게 적는다) 번호도 이어져야 한다.
      counterId: abstractId !== null ? `a${abstractId}` : `n${numId}`,
      levels: levels.size ? new Map([...base, ...levels]) : base,
      starts,
    });
  });

  return byNumId;
};

/**
 * 문단이 속한 목록 단계와, 그 문단이 몇 번째 항목인지를 세는 저울.
 *
 * 번호를 직접 세야 하는 이유: mammoth는 목록이 끊길 때마다(사이에 문단이나 표가 끼거나,
 * 다른 목록이 지나가거나) 새 <ol>을 연다. 그러면 Word에서 "6."이던 항목이 화면에서는
 * 다시 "1."이 된다. 문서 순서대로 세어 둔 번호를 <ol start>로 넘겨야 숫자가 맞는다.
 *
 * numId가 0이면 "목록 아님"이라는 뜻이다.
 */
const createNumberingCounter = (numbering) => {
  const counters = new Map(); // 목록 정의별 { 단계: 지금까지 센 번호 }
  const restarted = new Set(); // w:startOverride를 이미 쓴 (numId, 단계)

  return (props) => {
    if (!numbering.size || !(props.numId > 0)) return null;

    const definition = numbering.get(props.numId);
    if (!definition) return null;

    const ilvl = props.ilvl || 0;
    const level = definition.levels.get(ilvl);
    if (!level) return null;

    if (!counters.has(definition.counterId)) counters.set(definition.counterId, new Map());
    const counted = counters.get(definition.counterId);

    const restartKey = `${props.numId}:${ilvl}`;
    const override = definition.starts.get(ilvl);

    let number;
    if (override && !restarted.has(restartKey)) number = override;
    else number = counted.has(ilvl) ? counted.get(ilvl) + 1 : level.start;

    restarted.add(restartKey);
    counted.set(ilvl, number);

    // 상위 단계가 다시 나오면 하위 단계는 처음부터 센다 (1.1, 1.2 -> 2.1)
    Array.from(counted.keys()).forEach((key) => {
      if (key > ilvl) counted.delete(key);
    });

    return { ...level, number };
  };
};

// 문단을 감싼 가장 가까운 표의 스타일 id (표 스타일에도 정렬·글자 크기가 들어 있다)
const tableStyleId = (p) => {
  for (let node = p.parentNode; node; node = node.parentNode) {
    if (node.namespaceURI === W_NS && node.localName === "tbl") {
      return childVal(wordChild(node, "tblPr"), "tblStyle");
    }
  }
  return null;
};

// mammoth가 HTML 텍스트로 옮기는 자식만 모은다. 여기서 한 글자라도 어긋나면
// 런 단위 글자 크기가 엉뚱한 곳에 붙으므로, 옮겨지지 않는 것(w:br, w:instrText,
// w:delText)은 빼고 세어야 한다.
const RUN_TEXT_READERS = {
  t: (el) => el.textContent,
  tab: () => "\t",
  noBreakHyphen: () => "\u2011",
  softHyphen: () => "\u00ad",
};

const readRunText = (run) =>
  Array.from(run.childNodes).reduce((text, node) => {
    if (node.nodeType !== 1 || node.namespaceURI !== W_NS) return text;
    const read = RUN_TEXT_READERS[node.localName];
    return read ? text + read(node) : text;
  }, "");

// 문단이 직접 가진 런만. 텍스트 상자(w:txbxContent)는 런 안에 다시 문단을 품기 때문에,
// 후손을 전부 세면 남의 문단 글자까지 섞여 오프셋이 어긋난다.
const paragraphRuns = (p) =>
  Array.from(p.getElementsByTagNameNS(W_NS, "r")).filter((run) => {
    for (let node = run.parentNode; node; node = node.parentNode) {
      if (node.namespaceURI === W_NS && node.localName === "p") return node === p;
    }
    return false;
  });

// leftChars(글자 단위)가 있으면 twip 값을 이긴다 — Word 규칙이기도 하고,
// 한글 문서는 들여쓰기를 대부분 이쪽으로 적는다.
const paragraphIndent = (props, fontSize) => {
  if (props.indentChars > 0) {
    return Math.round((props.indentChars / CHAR_UNITS_PER_CHAR) * (fontSize || NARRATIVE_FONT_SIZE));
  }
  return props.indentTwips > 0 ? Math.round(props.indentTwips / TWIPS_PER_PX) : 0;
};

// 글머리 기호가 놓이는 자리 = 본문 들여쓰기 - 내어쓰기. Word의 목록은 기호를 본문보다
// hanging 만큼 왼쪽으로 내어 두기 때문에, 들여쓰기 값을 그대로 쓰면 기호가 한 칸 더 밀린다.
const listMarkerIndent = (props, fontSize) =>
  paragraphIndent(
    props.indentChars > 0
      ? { indentChars: props.indentChars - (props.hangingChars || 0) }
      : { indentTwips: (props.indentTwips || 0) - (props.hangingTwips || 0) },
    fontSize
  );

/**
 * 런에 걸리는 인라인 서식과 그것을 옮길 CSS 선언.
 *
 * 화면에서는 넷 다 textStyle 마크의 속성이라 span 하나에 함께 적어야 한다(span을 겹쳐 봐야
 * 마크 하나로 합쳐진다). 항목을 늘릴 때 이 표만 고치면 추출·병합·span 생성이 함께 따라온다.
 */
const RUN_STYLE_PROPS = [
  ["fontSize", (value) => `font-size: ${value}px`],
  ["color", (value) => `color: ${value}`],
  ["background", (value) => `background-color: ${value}`],
  ["fontFamily", (value) => `font-family: ${value}`],
];

// 서식 항목만 뽑아 낸다. 지정되지 않은 항목은 null로 채워 비교가 어긋나지 않게 한다.
const runStyleOf = (source) =>
  RUN_STYLE_PROPS.reduce((style, [key]) => ({ ...style, [key]: source[key] || null }), {});

const sameRunStyle = (a, b) => RUN_STYLE_PROPS.every(([key]) => a[key] === b[key]);

const hasRunStyle = (style) => RUN_STYLE_PROPS.some(([key]) => style[key]);

// 런 전체가 같은 값 하나로 되어 있으면 그 값, 아니면 null (섞여 있거나 지정이 없다)
const uniformValue = (values) =>
  values.length && values.every((value) => value && value === values[0]) ? values[0] : null;

/**
 * 문단별 들여쓰기·정렬과 런별 인라인 서식(RUN_STYLE_PROPS)을 문서 순서대로 추출한다.
 * mammoth는 전부 모델로는 읽지만 HTML로 내보내지 않는다.
 *
 * 서식은 낮은 것부터 덮어쓴다: 문서 기본값 → 표 스타일 → 문단 스타일 → 목록 단계 → 직접 지정.
 */
export const extractParagraphFormatting = (
  xmlDoc,
  styles = parseStyleDefinitions(null),
  numbering = parseNumberingDefinitions(null)
) => {
  if (!xmlDoc) return [];

  // 번호는 문서 전체를 순서대로 세어야 하므로 문단 하나하나가 아니라 여기서 들고 있는다
  const countList = createNumberingCounter(numbering);

  return Array.from(xmlDoc.getElementsByTagNameNS(W_NS, "p")).map((p) => {
    const pPr = wordChild(p, "pPr");
    const tableStyle = resolveStyle(styles, tableStyleId(p));
    const paragraphStyle = resolveStyle(styles, childVal(pPr, "pStyle"));
    const own = readParagraphProps(pPr);

    const inherited = {
      ...styles.defaults.paragraph,
      ...tableStyle.paragraph,
      ...paragraphStyle.paragraph,
    };

    // 목록 지정은 문단에 직접 적히기도, 스타일(개요 번호 매기기)에 담겨 있기도 하다
    const level = countList({ ...inherited, ...own });

    const paragraph = { ...inherited, ...(level ? level.paragraph : {}), ...own };

    // 문단 부호(w:pPr/w:rPr)의 크기도 런의 기본값으로 인정한다. Word에서 문단 전체를
    // 골라 크기를 바꾸면 런과 문단 부호에 같이 적히는데, 런 쪽이 비어 있는 문서가 있다.
    const runDefaults = {
      ...styles.defaults.run,
      ...tableStyle.run,
      ...paragraphStyle.run,
      ...readRunProps(wordChild(pPr, "rPr")),
    };

    const runs = paragraphRuns(p)
      .map((run) => {
        const rPr = wordChild(run, "rPr");
        const charStyle = resolveStyle(styles, childVal(rPr, "rStyle"));
        const resolved = { ...runDefaults, ...charStyle.run, ...readRunProps(rPr) };
        return { text: readRunText(run), ...runStyleOf(resolved) };
      })
      .filter((run) => run.text.length);

    return {
      text: normalizeText(runs.map((run) => run.text).join("")),
      indent: paragraphIndent(paragraph, runDefaults.fontSize),
      align: paragraph.align || null,
      // 목록 항목이면 기호 모양·기호가 놓이는 자리·문서에서 몇 번째 항목인지
      list: level
        ? {
            style: level.style,
            indent: listMarkerIndent(paragraph, runDefaults.fontSize),
            number: level.number,
          }
        : null,
      runs,
      // 문단이 한 서식으로만 되어 있으면 런 대응이 어긋나도 통째로 입힐 수 있다
      ...RUN_STYLE_PROPS.reduce(
        (style, [key]) => ({ ...style, [key]: uniformValue(runs.map((run) => run[key])) }),
        {}
      ),
    };
  });
};

/**
 * 셀별 테두리를 표 단위 규칙에서 미리 계산해 둔다.
 *
 * Word는 테두리를 표(w:tblBorders: 바깥 네 면 + 안쪽 가로/세로줄)에 적고 셀(w:tcBorders)에서
 * 덮어쓴다. 표 스타일(Table Grid 등)에만 적힌 경우도 많아 스타일까지 따라가야 한다.
 * 어디에도 없으면 Word는 선을 그리지 않으므로 "none"이다 — 지정을 비워 두면 화면의
 * 기본 테두리가 그려져 Word와 달라진다.
 */
const cellBorderMap = (xmlDoc, styles) => {
  const map = new Map();

  Array.from(xmlDoc.getElementsByTagNameNS(W_NS, "tbl")).forEach((tbl) => {
    const tblPr = wordChild(tbl, "tblPr");
    const style = resolveStyle(styles, childVal(tblPr, "tblStyle"));
    const table = { ...(style.table.borders || {}), ...readBorders(wordChild(tblPr, "tblBorders")) };

    // 직속 자식만 훑어 중첩 표의 행·열을 이 표의 것으로 세지 않는다
    const rows = wordChildren(tbl, "tr");
    rows.forEach((tr, rowIndex) => {
      const cells = wordChildren(tr, "tc");

      cells.forEach((tc, colIndex) => {
        const own = readBorders(wordChild(wordChild(tc, "tcPr"), "tcBorders"));
        const edge = {
          top: rowIndex === 0,
          bottom: rowIndex === rows.length - 1,
          left: colIndex === 0,
          right: colIndex === cells.length - 1,
        };

        const borders = {};
        Object.entries(BORDER_SIDES).forEach(([side, inside]) => {
          borders[side] = own[side] || table[edge[side] ? side : inside] || "none";
        });
        map.set(tc, borders);
      });
    });
  });

  return map;
};

// 셀 배경색·세로 맞춤·테두리를 문서 순서대로 추출 (병합으로 사라지는 셀은 건너뛴다)
export const extractCellFormatting = (xmlDoc, styles = parseStyleDefinitions(null)) => {
  if (!xmlDoc) return [];

  const borders = cellBorderMap(xmlDoc, styles);

  return Array.from(xmlDoc.getElementsByTagNameNS(W_NS, "tc")).reduce((acc, tc) => {
    const tcPr = wordChild(tc, "tcPr");

    const cell = {};
    if (tcPr) {
      // 가로/세로 병합의 연속 셀(restart가 아닌 것)은 HTML에 생성되지 않는다
      const hMerge = wordChild(tcPr, "hMerge");
      if (hMerge && getWordAttr(hMerge, "val") !== "restart") return acc;

      const vMerge = wordChild(tcPr, "vMerge");
      if (vMerge && getWordAttr(vMerge, "val") !== "restart") return acc;

      const shd = wordChild(tcPr, "shd");
      const fill = shd && getWordAttr(shd, "fill");
      if (fill && fill !== "auto") cell.background = `#${fill}`;

      const vAlign = WORD_VERTICAL_ALIGNMENTS[(childVal(tcPr, "vAlign") || "").toLowerCase()];
      if (vAlign) cell.verticalAlign = vAlign;
    }

    const cellBorders = borders.get(tc);
    if (cellBorders) cell.borders = cellBorders;

    acc.push(cell);
    return acc;
  }, []);
};

/**
 * 표가 원하는 폭(w:tblPr/w:tblW)을 twip으로. 못 읽으면 0.
 *
 * pct는 본문 폭에 대한 비율이라 페이지 폭을 모르면 풀 수 없다. auto/nil은 "내용에 맡긴다"는
 * 뜻이라 폭을 정하지 않는다.
 */
const readTableWidth = (tblPr, pageTwips) => {
  const tblW = wordChild(tblPr, "tblW");
  if (!tblW) return 0;

  const raw = (getWordAttr(tblW, "w") || "").trim();
  const type = (getWordAttr(tblW, "type") || "").toLowerCase();

  if (type === "pct") {
    const value = parseFloat(raw);
    if (!pageTwips || !(value > 0)) return 0;
    const percent = raw.endsWith("%") ? value : value / PCT_UNITS_PER_PERCENT;
    return Math.round((pageTwips * percent) / 100);
  }

  if (type === "dxa") {
    const twips = parseInt(raw, 10);
    return twips > 0 ? twips : 0;
  }

  return 0;
};

/**
 * 격자를 표가 원하는 폭에 맞춰 비율대로 늘리거나 줄인다.
 *
 * w:tblGrid는 열의 비율이고 실제 폭은 w:tblW가 정한다. 둘이 어긋나면 Word는 w:tblW를 따르는데,
 * 폭을 %로 적는 문서(프로그램이 만든 .docx가 대개 그렇다)는 격자에 자리 표시 값만 남겨 두기도
 * 한다. 격자를 그대로 옮기면 Word에서 페이지를 꽉 채우던 표가 화면에서는 몇 px짜리 기둥이 되어
 * 글자가 한 자씩 끊겨 왼쪽에 뭉친다.
 */
const fitGridToTableWidth = (twips, tableTwips) => {
  if (!tableTwips || !twips.length || twips.some((width) => !width)) return twips;

  const total = twips.reduce((sum, width) => sum + width, 0);
  if (total === tableTwips) return twips;

  return twips.map((width) => (width / total) * tableTwips);
};

// 표별 열 너비(w:tblGrid/w:gridCol, 단위 twip)를 문서 순서대로 px 배열로 추출.
// mammoth는 tblGrid를 통째로 무시하기 때문에 이 값이 없으면 모든 열이 균등 분할된다.
export const extractTableColumnWidths = (xmlDoc, pageWidth = 0) => {
  if (!xmlDoc) return [];

  return Array.from(xmlDoc.getElementsByTagNameNS(W_NS, "tbl")).map((tbl) => {
    const twips = wordChildren(wordChild(tbl, "tblGrid"), "gridCol").map((col) => {
      const width = intAttr(col, "w");
      return width > 0 ? width : 0;
    });

    // 비율을 먼저 맞추고 px로 옮긴다 (px로 먼저 반올림하면 좁은 격자에서 비율이 뭉개진다)
    const tableTwips = readTableWidth(wordChild(tbl, "tblPr"), pageWidth * TWIPS_PER_PX);
    return fitGridToTableWidth(twips, tableTwips).map((width) =>
      // 0은 "너비를 모른다"는 뜻이라 좁은 열이 반올림으로 0이 되지 않게 막는다
      width > 0 ? Math.max(1, Math.round(width / TWIPS_PER_PX)) : 0
    );
  });
};

// 표별 좌우 위치를 문서 순서대로 추출한다. 표 스타일에 적힌 위치도 따라간다.
export const extractTableAlignments = (xmlDoc, styles = parseStyleDefinitions(null)) => {
  if (!xmlDoc) return [];

  return Array.from(xmlDoc.getElementsByTagNameNS(W_NS, "tbl")).map((tbl) => {
    const tblPr = wordChild(tbl, "tblPr");
    const style = resolveStyle(styles, childVal(tblPr, "tblStyle"));
    return { ...style.table, ...readTableProps(tblPr) }.align || null;
  });
};

// 페이지 본문 폭(용지 폭 - 좌우 여백)을 px로. 표 너비를 화면에 비례로 옮기는 기준이다.
// 마지막 구역(w:body의 직속 w:sectPr)이 문서 본문의 기본 구역이다.
export const extractPageContentWidth = (xmlDoc) => {
  if (!xmlDoc || !xmlDoc.documentElement) return 0;

  const sectPr = wordChild(wordChild(xmlDoc.documentElement, "body"), "sectPr");
  if (!sectPr) return 0;

  const width = intAttr(wordChild(sectPr, "pgSz"), "w");
  if (!width) return 0;

  const pgMar = wordChild(sectPr, "pgMar");
  const twips = width - (intAttr(pgMar, "left") || 0) - (intAttr(pgMar, "right") || 0);
  return twips > 0 ? Math.round(twips / TWIPS_PER_PX) : 0;
};

/**
 * Word 열 너비를 편집 영역 기준으로 환산한다.
 *
 * Word의 본문 폭(A4 기준 약 600px)과 내러티브 본문 폭(808px)이 다르므로 twip을 그대로
 * px로 옮기면 Word에서 폭을 꽉 채우던 표가 화면에서는 왼쪽에 몰린 좁은 표가 된다.
 * 비율로 옮기면 꽉 찬 표는 꽉 차고, 절반짜리 표는 절반으로 남는다.
 * 페이지 폭을 못 읽으면(sectPr 없음) 원래 값을 그대로 쓴다.
 */
export const scaleColumnWidths = (widths, pageWidth, maxWidth) => {
  if (!widths || !pageWidth || !maxWidth) return widths;

  const scale = maxWidth / pageWidth;
  return widths.map((width) => (width > 0 ? Math.round(width * scale) : width));
};

// 열 너비를 편집 영역 안에 들어오도록 조정한다. 비율은 유지하고, 넘칠 때만 축소한다.
export const fitColumnWidths = (widths, maxWidth, minColumnWidth) => {
  if (!widths || !widths.length || widths.some((w) => !w)) return null;

  const total = widths.reduce((sum, w) => sum + w, 0);
  if (!maxWidth || total <= maxWidth) return widths;

  const ratio = maxWidth / total;
  return widths.map((w) => Math.max(minColumnWidth, Math.round(w * ratio)));
};

// 이어지는 런 중 서식이 같은 것끼리 묶는다 (span을 런 수만큼 만들지 않기 위해)
const mergeRuns = (runs) =>
  runs.reduce((segments, run) => {
    const last = segments[segments.length - 1];
    if (last && sameRunStyle(last, run)) last.text += run.text;
    else segments.push({ text: run.text, ...runStyleOf(run) });
    return segments;
  }, []);

const runStyleSpan = (doc, style) => {
  const span = doc.createElement("span");
  const declarations = RUN_STYLE_PROPS.filter(([key]) => style[key]).map(([key, css]) =>
    css(style[key])
  );
  span.setAttribute("style", declarations.join("; "));
  return span;
};

/**
 * 인라인 서식을 되살린다. FontSize·Color·BackgroundColor·FontFamily는 textStyle(인라인
 * span) 마크에서만 읽으므로 내용을 span으로 감싸야 한다.
 *
 * 런 단위로 감싸야 한 문단 안에 서식이 섞인 경우(제목 뒤에 붙은 주석 등)까지 살아남는다.
 * 다만 mammoth가 각주 표시나 필드처럼 원본에 없던 텍스트를 넣으면 글자 오프셋이 어긋나므로,
 * 텍스트가 정확히 일치할 때만 쪼개고 아니면 문단 전체를 한 서식으로 감싼다.
 */
const applyRunStyles = (doc, block, matched) => {
  const segments = mergeRuns(matched.runs);
  if (!segments.length || !block.firstChild) return;

  const textNodes = ownTextNodes(block);

  if (textNodes.map((node) => node.nodeValue).join("") !== segments.map((seg) => seg.text).join("")) {
    if (hasRunStyle(matched) && !block.querySelector(NESTED_BLOCKS)) {
      const span = runStyleSpan(doc, matched);
      while (block.firstChild) span.appendChild(block.firstChild);
      block.appendChild(span);
    }
    return;
  }

  if (segments.length === 1 && !hasRunStyle(segments[0])) return;

  // 텍스트 노드를 런 경계에서 잘라 가며 서식별 span으로 바꾼다
  let index = 0;
  let consumed = 0;

  textNodes.forEach((node) => {
    const pieces = [];
    let rest = node.nodeValue;

    while (rest.length && index < segments.length) {
      const segment = segments[index];
      const take = Math.min(segment.text.length - consumed, rest.length);
      pieces.push({ text: rest.slice(0, take), ...runStyleOf(segment) });
      rest = rest.slice(take);
      consumed += take;
      if (consumed >= segment.text.length) {
        index += 1;
        consumed = 0;
      }
    }
    if (rest.length) pieces.push({ text: rest, ...runStyleOf({}) });
    if (pieces.length === 1 && !hasRunStyle(pieces[0])) return;

    const fragment = doc.createDocumentFragment();
    pieces.forEach((piece) => {
      const text = doc.createTextNode(piece.text);
      if (!hasRunStyle(piece)) fragment.appendChild(text);
      else fragment.appendChild(runStyleSpan(doc, piece)).appendChild(text);
    });
    node.parentNode.replaceChild(fragment, node);
  });
};

// 항목을 감싼 목록의 수 (중첩 단계). mammoth는 하위 목록을 앞 항목 안에 넣는다.
const listDepth = (li) => {
  let depth = 0;
  for (let node = li.parentElement; node; node = node.parentElement) {
    if (node.tagName === "UL" || node.tagName === "OL") depth += 1;
  }
  return depth;
};

/**
 * 목록 항목에 기호 모양과 들여쓰기를 붙인다.
 *
 * 여백은 항목(li)에 붙여야 글머리 기호가 글자와 함께 움직인다. 안쪽 여백(padding)이나
 * 문단에 붙이면 글자만 밀리고 기호는 제자리에 남는다.
 *
 * Word의 들여쓰기는 왼쪽 여백에서 잰 절대값인데 HTML 쪽은 목록을 겹칠 때마다 이미
 * 한 칸씩(NARRATIVE_LIST_INDENT) 들어가 있으므로, 그만큼 뺀 나머지만 붙인다.
 */
// "1)" 목록의 번호는 CSS 카운터가 세므로, 번호를 옮길 때 카운터 시작점도 같이 옮겨야 한다
const counterReset = (number) => `counter-reset: ${NARRATIVE_LIST_COUNTER} ${number - 1}`;

const applyListFormatting = (block, list, expected) => {
  const parent = block.parentElement;
  const inList = parent && (parent.tagName === "UL" || parent.tagName === "OL");
  const declarations = [];

  if (list.style && inList) parent.setAttribute("data-list-style", list.style);

  /*
   * 번호를 Word에서 세던 값으로 맞춘다. mammoth는 목록이 끊기면 새 <ol>을 열고(그래서
   * "6."이 다시 "1."이 된다), 반대로 서로 다른 두 목록이 맞붙어 있으면 한 <ol>로 합친다
   * (그래서 두 번째 목록이 "1."부터 시작하지 않는다). 둘 다 번호가 어긋나는 자리다.
   *
   * 첫 항목은 목록의 start로, 중간에서 번호가 튀는 항목은 그 항목의 value로 적는다.
   * (counter-reset은 jsdom의 CSSOM이 버리므로 style 속성 문자열로 직접 적는다)
   */
  if (inList && parent.tagName === "OL" && list.number > 0) {
    const next = expected.get(parent);

    if (next === undefined) {
      if (list.number > 1) {
        parent.setAttribute("start", String(list.number));
        if (list.style === "paren") parent.setAttribute("style", counterReset(list.number));
      }
    } else if (list.number !== next) {
      block.setAttribute("value", String(list.number));
      // 목록 모양은 항목이 아니라 목록에 붙어 있어 항목만 봐서는 알 수 없다.
      // 카운터를 쓰지 않는 목록에서는 이 값이 아무 일도 하지 않으므로 늘 함께 적는다.
      declarations.push(counterReset(list.number));
    }

    expected.set(parent, list.number + 1);
  }

  const indent = list.indent - listDepth(block) * NARRATIVE_LIST_INDENT;
  if (indent > 0) declarations.push(`margin-left: ${indent}px`);
  if (declarations.length) block.setAttribute("style", declarations.join("; "));
};

/**
 * 추출한 문단 서식을 HTML 블록에 되돌려 붙인다.
 *
 * mammoth가 빈 문단을 통째로 버리기 때문에 인덱스만으로는 어긋난다. 텍스트가 일치하는
 * 문단을 앞으로 조금씩 건너뛰며 찾고, 못 찾으면 그 블록은 손대지 않는다.
 */
export const applyParagraphFormatting = (doc, paragraphs) => {
  if (!paragraphs.length) return;

  let index = 0;

  // 목록별로 "다음에 올 번호". 문서 순서대로 훑으므로 앞 항목이 남긴 값과 비교하면
  // 번호가 튀는 자리를 알 수 있다.
  const expected = new WeakMap();

  doc.querySelectorAll(PARAGRAPH_BLOCKS).forEach((block) => {
    const text = normalizeText(ownText(block));
    let matched = null;

    for (let offset = 0; offset <= PARAGRAPH_LOOKAHEAD && index + offset < paragraphs.length; offset += 1) {
      if (paragraphs[index + offset].text === text) {
        matched = paragraphs[index + offset];
        index += offset + 1;
        break;
      }
    }
    if (!matched) return;

    if (block.tagName === "LI") {
      // 목록은 기호 자리가 따로 있어 문단 들여쓰기를 그대로 쓸 수 없다
      if (matched.list) applyListFormatting(block, matched.list, expected);
    } else if (matched.indent) {
      block.style.marginLeft = `${matched.indent}px`;
    }

    // 정렬은 문단·제목 노드의 속성이라 li에 붙여 봐야 파싱 단계에서 버려진다.
    // 표 안이라면 셀에 붙인다. 에디터도 표 안에서는 셀 정렬만 쓰도록 되어 있고,
    // 문단 쪽에 남겨 두면 나중에 셀 정렬을 지정해도 문단이 이겨 버린다.
    if (matched.align && block.tagName !== "LI") {
      const cell = block.closest("td, th");
      // 셀 정렬은 셀 전체에 걸리므로 첫 문단이 정한다 (뒤 문단이 덮어쓰면 머리글이 흔들린다)
      if (!cell) block.style.textAlign = matched.align;
      else if (!cell.style.textAlign) cell.style.textAlign = matched.align;
    }

    applyRunStyles(doc, block, matched);
  });
};

// tiptap은 td/th에 블록 노드가 있어야 파싱하고, 빈 표는 스키마 위반이 된다
const sanitizeForTiptap = (doc) => {
  doc.querySelectorAll("td, th").forEach((cell) => {
    const hasBlock = Array.from(cell.children).some((c) => BLOCK_TAGS.has(c.tagName));
    if (!hasBlock) {
      const p = doc.createElement("p");
      p.innerHTML = cell.innerHTML;
      cell.innerHTML = "";
      cell.appendChild(p);
    }
  });

  doc.querySelectorAll("table").forEach((table) => {
    if (!table.querySelector("tr")) table.remove();
  });
};

// 원본 XML에서 뽑은 셀 서식·열 너비·표 위치를 변환된 HTML에 되돌려 붙인다
export const applyWordTableStyles = (
  doc,
  { cells, tableGrids, tableAligns = [], pageWidth, maxWidth, minColumnWidth }
) => {
  doc.querySelectorAll("td, th").forEach((cell, i) => {
    const format = cells[i];
    if (!format) return;
    if (format.background) cell.style.backgroundColor = format.background;
    if (format.verticalAlign) cell.style.verticalAlign = format.verticalAlign;

    // 테두리는 style 속성 문자열에 직접 붙인다. "border-right: none"은 CSSOM 구현에 따라
    // 조용히 버려지는데(jsdom이 그렇다), 그러면 Word에서 선이 없던 자리에 화면 기본
    // 테두리가 그려져 버린다.
    const borders = Object.entries(format.borders || {}).map(([side, value]) => `border-${side}: ${value}`);
    if (borders.length) {
      const current = (cell.getAttribute("style") || "").replace(/;\s*$/, "");
      cell.setAttribute("style", [current, ...borders].filter(Boolean).join("; "));
    }
  });

  // table.rows / row.cells는 중첩 표를 포함하지 않아 표 단위 매핑이 어긋나지 않는다
  doc.querySelectorAll("table").forEach((table, tableIndex) => {
    const margins = TABLE_ALIGN_MARGINS[tableAligns[tableIndex]];
    if (margins) {
      table.style.marginLeft = margins.left;
      table.style.marginRight = margins.right;
    }

    const scaled = scaleColumnWidths(tableGrids[tableIndex], pageWidth, maxWidth);
    const widths = fitColumnWidths(scaled, maxWidth, minColumnWidth);
    if (!widths) return;

    Array.from(table.rows).forEach((row) => {
      let col = 0;
      Array.from(row.cells).forEach((cell) => {
        const span = parseInt(cell.getAttribute("colspan"), 10) || 1;
        const slice = [];
        for (let i = 0; i < span; i += 1, col += 1) slice.push(widths[col] || minColumnWidth);
        cell.setAttribute("colwidth", slice.join(","));
      });
    });
  });
};

/**
 * .docx를 tiptap이 그대로 받을 수 있는 HTML로 변환한다.
 *
 * @param {ArrayBuffer} arrayBuffer 업로드된 .docx
 * @param {{ maxWidth: number, minColumnWidth: number }} options 표 열 너비 보정 기준
 * @returns {Promise<string>} 변환된 HTML
 */
export const convertWordDocument = async (arrayBuffer, { maxWidth, minColumnWidth }) => {
  // styleMap은 두 번째 인자(options)로 넘겨야 한다. 첫 인자(input)에 넣으면 조용히 무시된다.
  const [{ value: rawHtml }, xml] = await Promise.all([
    mammoth.convertToHtml({ arrayBuffer }, { styleMap: STYLE_MAP }),
    readWordXml(arrayBuffer),
  ]);

  const doc = new DOMParser().parseFromString(rawHtml, "text/html");
  const styles = parseStyleDefinitions(xml.styles);
  const numbering = parseNumberingDefinitions(xml.numbering);

  // 표 폭을 %로 적은 문서는 페이지 본문 폭이 있어야 열 너비를 풀 수 있다
  const pageWidth = extractPageContentWidth(xml.document);

  // sanitize가 셀 안에 <p>를 새로 만들 수 있으므로 문단 대응은 그 전에 끝낸다
  applyParagraphFormatting(doc, extractParagraphFormatting(xml.document, styles, numbering));
  sanitizeForTiptap(doc);
  applyWordTableStyles(doc, {
    cells: extractCellFormatting(xml.document, styles),
    tableGrids: extractTableColumnWidths(xml.document, pageWidth),
    tableAligns: extractTableAlignments(xml.document, styles),
    pageWidth,
    maxWidth,
    minColumnWidth,
  });

  return doc.body.innerHTML;
};
